"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useLocation } from "@/contexts/LocationContext";
import { getLocalityAndState } from "@/utils/location";
import { stateSlugFromName } from "@/lib/seo/property-urls";

interface NearbyCity {
  name: string;
  slug: string;
  count: number;
}

// Progressive-enhancement widget: silently renders nothing if location
// permission isn't granted, geocoding fails, or no nearby cities are found.
export default function NearbyCities() {
  const { userLocation } = useLocation();
  const [detectedCity, setDetectedCity] = useState<string | null>(null);
  const [stateSlug, setStateSlug] = useState<string | null>(null);
  const [cities, setCities] = useState<NearbyCity[]>([]);

  useEffect(() => {
    if (!userLocation) return;
    let cancelled = false;

    (async () => {
      try {
        const { city, state } = await getLocalityAndState(
          userLocation.lat,
          userLocation.lng,
        );
        if (cancelled || !state) return;

        const params = new URLSearchParams({ state });
        if (city) params.set("exclude", city);

        const res = await fetch(`/api/nearby-cities?${params}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();

        if (!cancelled && Array.isArray(data.cities) && data.cities.length) {
          setStateSlug(stateSlugFromName(state));
          setDetectedCity(city);
          setCities(data.cities);
        }
      } catch {
        // Silently skip on any geocoding/network failure.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userLocation]);

  if (!stateSlug || cities.length === 0) return null;

  return (
    <nav
      aria-label="Properties near you"
      className="mx-auto mb-6 max-w-6xl px-4"
    >
      <span className="mr-3 font-medium text-gray-900">
        {detectedCity
          ? `Near ${detectedCity}, browse properties in:`
          : "Properties near you:"}
      </span>
      <ul className="mt-2 flex flex-wrap gap-2.5 text-sm">
        {cities.map((city) => (
          <li key={city.slug} className="inline">
            <Link
              className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-4 py-2 font-medium leading-5 text-teal-900 shadow-sm transition-colors hover:border-teal-400 hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
              href={`/properties/in/${stateSlug}/${city.slug}`}
            >
              {city.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
